/* eslint-disable no-restricted-imports */
import React, {useMemo, useRef} from 'react'
import {UIGameActionButton} from '../../../common/UIGameActionButton/index.ts'
import {
    UIGameResourceCardsTooltipContent,
    type UIGameResourceCardsTooltipContentHandle,
} from '../../../common/UIGameResourceCardsTooltipContent/index.ts'
import {getCardsForPiece} from '@colonist/game-card-types'
import {PieceTypes} from '@colonist/game-base-types'
import {useActionButtonCommonErrors} from '../utils.ts'
import {SETTLEMENT_IMAGE_PATH} from './utils.ts'
import {useGameStore} from '../../../store/GameStoreContext.ts'
import {usePlayerValidators} from '../../../store/selectors/usePlayerValidators.ts'
import {ActionState} from '@colonist/game-states'
import {type TranslationObject} from '@colonist/i18n'
import {useUIGameSocketContext} from '../../../contexts/UIGameSocketContext.tsx'
import {useCurrentStateValidator} from '../../../store/selectors/useCurrentStateValidator.ts'
import {HiddenUIElement} from '@colonist/game-beginner-mode'
import clsx from 'clsx'
import styles from '../UIGameActionButtonContainer.module.scss'
import {useMechanicValidatorSettlement} from '../../../store/selectors/useMechanicValidatorSettlement.ts'

const cardsToBuildSettlement = getCardsForPiece(PieceTypes.Settlement)

export const UIGameActionButtonSettlement = ({
    className,
}: {
    className?: string
}) => {
    const myColor = useGameStore((state) => state.myColor)
    const {getPlayerValidator} = usePlayerValidators()
    const currentStateValidator = useCurrentStateValidator()
    const beginnerActionState = useGameStore((state) => state.gameBeginnerHint.beginnerActionState)
    const tooltipRef = useRef<UIGameResourceCardsTooltipContentHandle>(null)
    const playerValidator = getPlayerValidator(myColor)
    const {errorHelperText, hasCommonErrors} = useActionButtonCommonErrors()
    const {sendBuildSettlement, sendCancelAction, sendClearCornerHighlight} = useUIGameSocketContext()
    const settlementValidator = useMechanicValidatorSettlement()

    const actionStatus = useMemo(() => {
        if(!settlementValidator) {
            return {canTakeAction: false}
        }
        const useResources = true
        const canPlaceSettlementActionStatus = settlementValidator.canPlayerPlaceSettlement(myColor, useResources)

        return canPlaceSettlementActionStatus
    }, [settlementValidator, myColor])

    const triggerFlickerCardsAnimation = () => {
        const notEnoughResources = !playerValidator.hasResourceCardsInHand(cardsToBuildSettlement)
        if(notEnoughResources) {
            tooltipRef.current?.triggerFlickerAnimation()
        }
    }

    const isHidden = beginnerActionState?.hiddenUIElements.includes(HiddenUIElement.SettlementActionButton)
    const disabled = hasCommonErrors || !actionStatus.canTakeAction || isHidden
    const actionState = currentStateValidator.getPlayerActionState(myColor)
    const isPlacingSettlement = actionState === ActionState.PlaceSettlement && !disabled
    const bankSettlementAmount = settlementValidator?.getSettlementPlayerState(myColor).bankSettlementAmount ?? 0
    const helperText: TranslationObject | undefined = errorHelperText || actionStatus.errorMessageIfCantTakeAction
    const icon = SETTLEMENT_IMAGE_PATH[myColor]

    const onClick = () => {
        if(disabled) return
        if(isPlacingSettlement) {
            sendClearCornerHighlight()
            sendCancelAction()
        } else {
            sendBuildSettlement()
        }
    }

    return <UIGameActionButton
        className={clsx(className, {
            [styles.hidden]: isHidden,
        })}
        count={bankSettlementAmount}
        countColor={myColor}
        disabled={disabled}
        helperText={helperText}
        highlight={isPlacingSettlement}
        iconPath={icon}
        onClickActionIfDisabled={triggerFlickerCardsAnimation}
        tooltipCustomMenuComponent={
            <UIGameResourceCardsTooltipContent
                cardInventory={playerValidator.resourceCards.cards}
                cardsToBuild={cardsToBuildSettlement}
                ref={tooltipRef}/>
        }
        onClick={onClick}
    />
}
